import { useState, useEffect, useRef } from 'react';
import showdown from 'showdown';
import { stripHtml } from 'string-strip-html';

export const useTypewriter = (markdownText: string | null, speed = 50, isEditorMode = false, delay = 350) => {
  const [displayText, setDisplayText] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const displayTextRef = useRef('');
  const converter = new showdown.Converter({ openLinksInNewWindow: true });

  const resetTyping = async () => {
    await new Promise((resolve) => setTimeout(resolve, delay));

    setDisplayText('');
    displayTextRef.current = '';
    setTypingDone(false);
  };

  useEffect(() => {
    if (markdownText === null) {
      setDisplayText('');
      displayTextRef.current = '';
      return;
    }

    const htmlText = converter.makeHtml(markdownText);
    const plainText = stripHtml(htmlText).result;

    if (isEditorMode && displayText.length) {
      setTypingDone(true);
      setDisplayText(htmlText);
      return;
    }

    let i = 0;
    setTypingDone(false);
    displayTextRef.current = '';
    const typingInterval = setInterval(() => {
      if (i < plainText.length) {
        displayTextRef.current += plainText.charAt(i);
        setDisplayText(displayTextRef.current);
        i++;
      } else {
        clearInterval(typingInterval);
        if (plainText.length > 0) {
          setTypingDone(true);
          setDisplayText(htmlText); // Switch to HTML content after typing is done
        }
      }
    }, speed);

    return () => {
      clearInterval(typingInterval);
    };
  }, [markdownText, speed]);

  return { text: displayText, typingDone, resetTyping };
};